// Match simulation: heroes, orders, combat, statuses, capture points, score, vision, bots.
// Ported from prototypes/match/js/sim.js. Rendering (`drive()`, rig, ring) is cut; the view (stage 1b)
// reads state each tick and consumes `game.events` (fx + damage numbers + anim) instead.
import * as THREE from 'three';
import { LAYOUT, inBrush, baseParts, solid, lineOfSight, groundSpeed, type Team, type VisionSource } from './layout.js';
import type { NavGrid, Point } from './nav.js';
import { HEROES, type HeroDef, type HeroKind } from './heroes.js';
import { SKILLS, castSkill, updateSkill, aimCharge, releaseCharge } from './skills.js';
import {
  ARTIFACTS, canCastArtifact, castArtifact, buyArtifact, sellArtifact, updateTotems, strikeTotem,
  maxHpOf, attackSpeedMultOf, passiveMoveSpeedBonusOf, outOfCombatRegenOf,
  cooldownRateOf, basicMissChanceOf, respawnMultOf, dragonToothMultOf, captureMultOf,
  type ArtifactSlot, type DamageMark, type Totem, type ArtifactLock,
} from './artifacts.js';
import {
  buyRelic, castRelic, canCastRelic, loseRelicOnDeath, updateRelics, relicMoveSpeedBonusOf, relicAtkSpeedBonusOf,
  relicRegenOf, eyeOpen, eyeDmgMultOf, eyeCdRateOf, type RelicHold,
} from './relics.js';
import { INK } from './economy.js';
import type { FxEvent, GameEvent, HeroId, Vec2 } from './events.js';
import type { Command, SkillKey, ArtifactSlotKey, ArtifactLine, RelicLine } from './commands.js';
import { mulberry32, type Rng } from './rng.js';
import { botThink } from './botBrain.js';
import { StatsTracker } from './stats.js';
import { PASSIVES } from './passives.js';
import {
  spawnMonsters, respawnMonster, updateMonster, damageMonster, updateTeamBuffs, monsterVisibleTo, isMonster, isSummon, isHero, bulkOf, noTeamBuffs,
  NO_BOON, MONSTERS, type Monster, type Target, type TeamBuffs, type Boon,
} from './monsters.js';
import { updateSummons, damageSummon, summonVisibleTo, birdSight, countBirdSight, type Summon } from './summons.js';
import { holdBonds, type Bond } from './bind.js';

export { SKILLS, ARTIFACTS };
export type { Monster, Target, TeamBuffs, Boon, Summon };

export const RULES = {
  pointTick: 4, win: 500, matchTime: 900,
  countdown: 3,    // pre-match countdown, seconds: shown before tick 0, never simulated (server/match.ts, client main.ts)
  killScore: 15,   // a hero kill scores for the killer's team
  baseRegen: 0.01,  // share of max hp healed per second, always
  captureSolo: 8, captureDuo: 5,
  respawnBase: 6, respawnPerMinute: 1, respawnMax: 12,
  fountainRadius: 4.5, fountainHeal: 45,
  sight: 13, baseSight: 16, brushSight: 3, revealAfterAttack: 1.5,
};

export const STEP_DT = 1 / 60;

/* Which team acts first on this tick. A tick is resolved hero by hero, so the first mover wins every exact
   tie — a lethal swing lands before the victim's own, the second mover reacts to the first one's new
   position, an instant cast from think() hits before the other side's — and a fixed roster order handed
   all of them to team a (mirror matches: 52–55% for a). A hash of (tick, seed) gives each tick to either
   team with even odds: deterministic from seed + tick like everything else, not locked to tick parity (a
   cooldown of an even number of ticks would keep landing on the same side), and salted so the ticks where
   every match's first fight happens aren't decided the same way in every match. */
export function teamBFirst(tick: number, seed: number): boolean {
  let x = Math.imul((tick ^ Math.imul(seed, 0x9E3779B1)) >>> 0, 0x85EBCA6B);
  x ^= x >>> 13; x = Math.imul(x, 0xC2B2AE35); x ^= x >>> 16;
  return (x & 1) === 1;
}

/* Lag compensation: "where was this hero, as that shooter saw them?" — an optional port core consults
   only when deciding whether a shot connects, never for continuous effects (pools, zones, auras, capture
   points). Returns the target's rewound position and whether it was alive at that instant, or nothing to
   mean "use the present". The port only ever answers about positions: it cannot resurrect anyone, change
   hp, or otherwise feed back into the simulation — see Game.rewind(), the only thing that calls it.

   Consequence worth knowing before assuming otherwise: with a port injected, the match is no longer
   reproducible from seed + commands alone. A real port derives its answers from wall-clock latency
   measurements external to the deterministic command stream, so the same seed and the same commands can
   resolve different hits on different runs. Server-side replay, demo recording and after-the-fact cheat
   verification all require running with lagCompensation absent (or a recorded, replayable stand-in for
   it) — they are not available while live compensation is active. */
export type SeenTarget = { x: number; z: number; alive: boolean };
export type LagCompensation = (shooterId: HeroId, targetId: HeroId) => SeenTarget | null;

export type Order =
  | { type: 'move' }
  | { type: 'attack'; target: Target }
  | { type: 'attackTotem'; target: number }  // totem id, not a Totem reference — see PendingTotem
  | { type: 'follow'; target: Hero };

export type Slow = { pct: number; until: number };
export type Buff = { type: 'haste' | 'dmg'; val: number; until: number };
export type Hot = { perSec: number; until: number; tickAt?: number; src?: Hero };
export type Shield = { amount: number; until: number; src: Hero | null };
export type Taunt = { by: Hero; until: number };
// Соблазн (charm: walks to `by`) and Стая's fear (runs to its own base): no orders, no casts, no attacks.
export type Forced = { kind: 'charm' | 'fear'; by: Hero; until: number;
  /** Соблазн: share of the charmed hero's max hp `by` draws into himself per second, in DRAIN_TICK steps. */
  drain?: number; drainAt?: number };
const DRAIN_TICK = 0.5;
// Липкое пламя's afterburn: whole ticks left rather than an end time, so the count never drifts on float sums.
export type Burn = { src: Hero; ticks: number; tickAt: number };
export type Pull = { from: THREE.Vector3; to: THREE.Vector3; t0: number; dur: number; by?: Hero };
export type Dash = { dir: THREE.Vector3; left: number; hit?: Set<Target>; mult?: number; from?: THREE.Vector3 };
export type Hop = { from: THREE.Vector3; to: THREE.Vector3; dur: number; arc?: number };
export type Charge = { t0: number; dir: THREE.Vector3 };
export type Channel = { dir: THREE.Vector3; until: number; tickAt: number };
export type Cast = { time: number; fn: () => void };
export type Pending = { at: number; target: Target; mult?: number };
// A totem id, not a live Totem reference: identity comparisons (Array.includes) on a reference would
// break the instant that state crosses a serialization boundary, exactly like DamageMark.by.
export type PendingTotem = { at: number; totemId: number };
export type BotState = {
  thinkAt: number; retreat: boolean; lane: string | null; shopping: boolean;
  // botBrain.ts memory, absent until it first thinks: the sticky target and the fight-retreat hysteresis.
  focus?: HeroId | null; focusUntil?: number; disengaged?: boolean; disengagedAt?: number;
  goal?: string; hunt?: number | null; huntSince?: number;  // botGoals.ts: the point headed for, the monster being taken
};

export type HeroState =
  | 'normal' | 'brace' | 'charging' | 'windup' | 'dash' | 'hop' | 'feint' | 'leap'
  | 'lungeWindup' | 'lunge' | 'channel' | 'casting' | 'recover' | 'flock';

export interface Hero {
  id: HeroId;
  kind: HeroKind;
  def: HeroDef;
  team: Team;
  player: boolean;
  pos: THREE.Vector3;
  yaw: number;
  wantYaw: number;
  snapTurn?: boolean;
  lift: number;
  hp: number;
  alive: boolean;
  respawnAt: number;
  diedAt?: number;
  order: Order | null;
  path: Point[];
  repathAt: number;
  cd: { attack: number; Q: number; W: number };
  pending: Pending | null;
  pendingTotem: PendingTotem | null;
  swingAt: number;
  clipFlip: number;
  state: HeroState;
  stateT: number;
  stunUntil: number;
  revealUntil: number;
  invulnUntil: number;
  slows: Slow[];
  shield: Shield | null;
  kb: THREE.Vector3;
  buffs: Buff[];
  hots: Hot[];
  taunt: Taunt | null;
  pull: Pull | null;
  channel: Channel | null;
  dash: Dash | null;
  hop: Hop | null;
  charge: Charge | null;
  cast: Cast | null;
  recoverFor: number;
  chainAt?: number;
  braceArmed: boolean;
  empowered: boolean;
  feintArmed: boolean;
  bot: BotState | null;
  /* ---- artefacts (docs/game-design.md, "Артефакты") ---- */
  artifacts: { E: ArtifactSlot | null; R: ArtifactSlot | null };
  marks: DamageMark[];           // Клеймо: % extra damage taken from everyone
  cloakUntil: number;            // Пергаментный плащ: distinct from invulnUntil, gates attack/cast specifically
  // Целебная мазь's own clock: the last sim time this hero actually took damage, direct or zone/DOT alike
  // (see Game.damage()) — never reset except at spawn/respawn, so "out of combat" is always a plain
  // `t - lastDamageAt >= quiet` read, with no separate in-combat flag to keep in sync.
  lastDamageAt: number;
  // Derived from Game.teamBuffs by monsters.ts applyBoon() — never written anywhere else.
  boon: Boon;
  /* ---- hero passives (passives.ts) ---- */
  stillSince: number;                // last sim time this hero's position changed during its own update (Сошка); Тень's own attack/cast too (Без отражения)
  revealedTo: Record<Team, number>;  // Звездочёт: visible to that team until then, fog and brush alike
  burn: Burn | null;
  /* ---- «Астрал» ---- */
  stealthUntil: number;              // Слиться: hidden from the enemy outright until then (or until it breaks)
  ambushLeft: number;                // Слиться: empowered swings still owed after breaking stealth by attacking
  ambushUntil: number;               // …and until when they may be spent
  forced: Forced | null;
  wallShadow: Zone | null;           // Тень на стене: the marker she swaps to when its delay runs out
  sinHitAt: number;                  // Первородный: the last time an enemy Грех's hit connected on this hero
  /* ---- «Заклинатели» ---- */
  wolfUntil: number;                 // Обернуться: melee basic attack at SKILLS.shewolf.W.range until then
  tether: Bond | null;               // Связка (bind.ts); both heroes of a tied pair share one Bond
}

export type RosterEntry = { kind: HeroKind; team: Team; player?: boolean };

const v3 = (x: number, z: number) => new THREE.Vector3(x, 0, z);
// inSight() packs Math.round(coord * 4) pairs into one exact integer key while both are below this.
const SIGHT_KEY_SPAN = 2 ** 20;
const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const yawTo = (from: { x: number; z: number }, to: { x: number; z: number }) => Math.atan2(to.x - from.x, to.z - from.z);
const angDiff = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));
// Exported: the view's drive() needs the same "is this hero considered moving" set for base-clip choice.
export const MOBILE_STATES = new Set<HeroState>(['normal', 'brace', 'charging']);
// Knockback velocity decays as exp(-KB_DECAY·t) (see updateHero).
const KB_DECAY = 6;
const live = <T extends { until: number }>(list: T[], t: number) => list.filter(x => x.until > t);

export type Point3 = { id: string; pos: THREE.Vector3; progress: number; owner: Team | null; contested: boolean };
export type Projectile = {
  kind: 'homing' | 'linear';
  src: Hero; target?: Target; totemId?: number; pos: THREE.Vector3; speed: number; dmg: number; color?: number; live: boolean;
  blame?: Hero;  // Наваждение: a confused hero's bolt at its own ally — damage() credits this hero instead of `src`
  dir?: THREE.Vector3; left?: number; width?: number; big?: boolean; look?: string;
  onHit?: (hit: Target) => void;
  // Сокол на перчатке (`look === 'falcon'`): a purely visual climb-and-dive arc, never read by hit/damage
  // logic. `arcFrom` is the flat distance to the target at the moment updateProjectiles first sees this
  // bolt (lazily captured there, not at spawn, so it needs no extra field at every homing call site).
  // `arc` is the 0..1 flight progress derived from it each tick, kept monotonic (never decreases even if
  // the target briefly outruns the bolt) so the client's arc height never jitters. See fx.ts's falconArc.
  arcFrom?: number; arc?: number;
};
export type Lob = { src: Hero; from: THREE.Vector3; to: THREE.Vector3; t0: number; flight: number; r: number; onLand: (pt: THREE.Vector3) => void; live: boolean };
export type Zone = {
  pos: THREE.Vector3; team: Team; src: Hero; r: number; until: number; life: number; tickAt: number;
  heal?: number; dmg?: number; slow?: number; look?: string; follow?: Hero; vel?: THREE.Vector3; reveal?: boolean; immune?: boolean; tickAt0?: number;
  healReduction?: number;  // Смоляная бутыль: multiplies every heal an enemy standing inside receives
  burn?: boolean;          // Липкое пламя: a connecting tick ignites the afterburn
  share?: number;          // Расплата: this share of what allies inside take is taken off them…
  taken?: number;          // …and `src` himself takes this share of the original hit (≤ share)
  confuse?: boolean;       // Наваждение: enemies inside may swing at their own ally
  hideWithSrc?: boolean;   // Тень на стене: the enemy doesn't see it while `src` is under Слиться
};
// `sight` (how close an enemy must be to reveal an armed trap) is view-only, per skills.ts's `...sk.trap`
// spread, exactly like heroes.ts's visual-only hero fields.
export type Trap = { pos: THREE.Vector3; team: Team; src: Hero; armAt: number; until: number; r: number; dmg: number; slow: number; slowFor: number; life: number; sight?: number; fired?: boolean };
export type Barrier = { center: THREE.Vector3; dir: THREE.Vector3; half: number; team: Team; until: number };
export type Reveal = { x: number; z: number; r: number; team: Team; until: number; t0: number; src?: Hero; hpPerSec?: number; tickAt?: number };

export class Game {
  nav: NavGrid;
  /** The terrain's walking-speed factor at a position (a ford's slow); tests swap in another map's. */
  groundSpeed: (x: number, z: number) => number = groundSpeed;
  rng: Rng;
  t = 0;
  points: Point3[];
  score = { a: 0, b: 0 };
  /** Each team's shared ink pool (economy.ts): only that team is ever told its own (server/snapshot.ts buildYou). */
  ink = { a: 0, b: 0 };
  clock = 0;
  tickAcc = 0;
  winner: Team | null = null;
  winnerT = 0;
  overtime = false;
  projectiles: Projectile[] = [];
  lobs: Lob[] = [];
  pools: Zone[] = [];
  traps: Trap[] = [];
  barriers: Barrier[] = [];
  reveals: Reveal[] = [];
  totems: Totem[] = [];
  artifactLocks: ArtifactLock[] = [];
  /** Each team's relic (relics.ts), null while it holds none. */
  relics: Record<Team, RelicHold | null> = { a: null, b: null };
  /** Око Бестиария: that team sees the whole map until then. */
  eyeUntil: Record<Team, number> = { a: 0, b: 0 };
  nextTotemId = 0;
  events: GameEvent[] = [];
  timers: { at: number; fn: () => void }[] = [];
  heroes: Hero[];
  player: Hero | undefined;
  monsters: Monster[];
  /** Summoned creatures (summons.ts), in summoning order; the dead are dropped at the end of their tick. */
  summons: Summon[] = [];
  nextSummonId = 0;
  teamBuffs: TeamBuffs = noTeamBuffs();
  readonly stats: StatsTracker;
  // Its own stream: monster wandering must never shift the bots' g.rng coin flips.
  readonly monsterRng: Rng;
  // Наваждение's coin flips: their own stream, so a confused swing never shifts the bots' g.rng.
  readonly confuseRng: Rng;
  // Туманный амулет's miss rolls: their own stream, drawn only for a basic attack on a holder.
  readonly missRng: Rng;
  // false = puppets (no AI, immune): for a client that only mirrors monsters and summons from the server.
  readonly monsterAi: boolean;
  private readonly seed: number;
  // Roster order and the same with team b's heroes first (each team keeps its own internal order).
  private readonly turnOrders: [Hero[], Hero[]];
  private sightT = -1;
  private readonly sightCache = new Map<number, boolean>();
  private readonly sightCacheStr = new Map<string, boolean>();
  private readonly eventSubjects = new WeakMap<GameEvent, Hero | Summon>();
  private readonly hitAttackers = new WeakMap<GameEvent, Target>();

  private readonly lagCompensation?: LagCompensation;

  constructor(nav: NavGrid, roster: RosterEntry[], opts: { seed?: number; lagCompensation?: LagCompensation; monsterAi?: boolean } = {}) {
    this.nav = nav;
    this.seed = opts.seed ?? 1;
    this.rng = mulberry32(this.seed);
    this.monsterRng = mulberry32(((opts.seed ?? 1) ^ 0x9E3779B9) >>> 0);
    this.confuseRng = mulberry32(((opts.seed ?? 1) ^ 0x5BD1E995) >>> 0);
    this.missRng = mulberry32(((opts.seed ?? 1) ^ 0x27D4EB2F) >>> 0);
    this.monsterAi = opts.monsterAi ?? true;
    this.lagCompensation = opts.lagCompensation;
    this.points = LAYOUT.points.map(p => ({ id: p.id, pos: v3(...p.pos), progress: 0, owner: null, contested: false }));
    this.stats = new StatsTracker(roster.length);
    this.heroes = roster.map((r, i) => this.spawnHero(r, i));
    this.player = this.heroes.find(h => h.player);
    this.turnOrders = [this.heroes, [...this.heroes.filter(h => h.team === 'b'), ...this.heroes.filter(h => h.team !== 'b')]];
    this.monsters = spawnMonsters(this);
  }

  /* Consulted only at the moment a hit is decided (basic attacks, homing/linear projectile collision,
     direction/point skillshots) — never for continuous effects. A target already dead now is never
     hittable regardless of what the port says; the port's own `alive: false` means it was dead at the
     rewound instant, which is just as final. Absent port, or the port declining to answer, means "use
     the present" — this is what keeps every call site bit-identical with no port injected. */
  rewind(shooter: Hero, target: Target): SeenTarget {
    if (!target.alive) return { x: target.pos.x, z: target.pos.z, alive: false };
    if (!isHero(target)) return { x: target.pos.x, z: target.pos.z, alive: true };  // the port only knows heroes
    return this.lagCompensation?.(shooter.id, target.id) ?? { x: target.pos.x, z: target.pos.z, alive: true };
  }

  private spawnHero({ kind, team, player }: RosterEntry, i: number): Hero {
    const def = HEROES[kind];
    const h: Hero = {
      id: i, kind, def, team, player: !!player,
      pos: new THREE.Vector3(), yaw: 0, wantYaw: 0, lift: 0,
      hp: def.hp, alive: true, respawnAt: 0,
      order: null, path: [], repathAt: 0,
      cd: { attack: 0, Q: 0, W: 0 }, pending: null, pendingTotem: null, swingAt: -9, clipFlip: 0,
      state: 'normal', stateT: 0, stunUntil: 0, revealUntil: 0, invulnUntil: 0,
      slows: [], shield: null, kb: new THREE.Vector3(), buffs: [], hots: [], taunt: null, pull: null, channel: null,
      dash: null, hop: null, charge: null, cast: null, recoverFor: 0,
      braceArmed: false, empowered: false, feintArmed: false,
      bot: player ? null : { thinkAt: 0, retreat: false, lane: null, shopping: false },
      artifacts: { E: null, R: null }, marks: [], cloakUntil: 0, lastDamageAt: -Infinity, boon: { ...NO_BOON },
      stillSince: 0, revealedTo: { a: 0, b: 0 }, burn: null,
      stealthUntil: 0, ambushLeft: 0, ambushUntil: 0, forced: null, wallShadow: null, sinHitAt: -Infinity,
      wolfUntil: 0, tether: null,
    };
    this.placeAtBase(h, i);
    return h;
  }

  private placeAtBase(h: Hero, i: number) {
    const b = baseParts(h.team), [x, z] = b.spawn(i);
    h.pos.set(x, 0, z);
    h.yaw = h.wantYaw = b.facing;
  }

  /* Enemy heroes a skill may touch: a silhouette (Без отражения) is beyond every hit, aimed or not. */
  enemiesOf(h: Hero) { return this.heroes.filter(e => e.team !== h.team && e.alive && !this.silhouette(e)); }
  /* Everything an enemy-damaging skill may hit: enemy heroes, then monsters (a returning one is immune), then
     enemy summons. */
  hostilesOf(h: Hero): Target[] {
    return [...this.enemiesOf(h), ...this.monsters.filter(m => m.alive && m.state !== 'return'), ...this.summons.filter(s => s.alive && s.team !== h.team)];
  }

  /* `subject`: the hero an event happens ON (a hit, a trap going off under him). Such an event is only as
     visible as that hero — grass and Слиться included — not merely as its spot; see subjectOf(). Kept off
     the event itself so the wire shape doesn't change. */
  emit(e: GameEvent, subject?: Target) {
    this.events.push(e);
    if (subject && !isMonster(subject)) this.eventSubjects.set(e, subject);
  }
  /* A summon subject is held here even after it dies and leaves g.summons, so an event about its killing blow
     is still judged by where it stood. */
  subjectOf(e: GameEvent): Hero | Summon | undefined { return this.eventSubjects.get(e); }
  /* Whether `team` sees an event's subject: a hero by visibleTo(), a summon by the totem rule. */
  subjectVisibleTo(team: Team, s: Hero | Summon): boolean { return isSummon(s) ? summonVisibleTo(this, team, s) : this.visibleTo(team, s); }
  /* fx `hit` named after `by`, the unit that dealt it. The event is as visible as its victim; who swung is
     told only to a team that sees the swinger too — see eventFor(). */
  emitHit(e: Extract<FxEvent, { name: 'hit' }>, subject: Target | undefined, by: Target | null | undefined) {
    if (by) { e.by = by.kind; this.hitAttackers.set(e, by); }
    this.emit(e, subject);
  }
  /* Whether `team` sees `e`'s victim but not the attacker its `by` names (a knight swinging from grass). */
  attackerHidden(team: Team, e: GameEvent): boolean {
    const a = this.hitAttackers.get(e);
    return !!a && !(isMonster(a) ? monsterVisibleTo(this, team, a) : this.subjectVisibleTo(team, a));
  }
  /* `e` as `team` is told it: without `by` when the attacker is out of its sight. Never mutates `e` (shared by both teams). */
  eventFor(team: Team, e: GameEvent): GameEvent {
    if (!this.attackerHidden(team, e)) return e;
    const { by: _, ...told } = e as Extract<FxEvent, { name: 'hit' }>;
    return told;
  }
  pos2(v: { x: number; z: number }): Vec2 { return { x: v.x, z: v.z }; }
  emitAnim(hero: HeroId, clips: string[], fade: number, opts: { unless?: string[]; skipIfBusy?: boolean; resetBase?: boolean } = {}) {
    const { unless, skipIfBusy, resetBase } = opts;
    this.emit({ type: 'anim', hero, clips, fade, ...(unless ? { unless } : {}), ...(skipIfBusy ? { skipIfBusy } : {}), ...(resetBase ? { resetBase } : {}) });
  }
  /* Run fn after `delay` seconds of game time (multi-hit skills timed to clip frames). */
  later(delay: number, fn: () => void) { this.timers.push({ at: this.t + delay, fn }); }

  /* ---------------- vision ---------------- */
  visionSources(team: Team): VisionSource[] {
    const [bx, bz] = LAYOUT.bases[team], out: VisionSource[] = [{ x: bx, z: bz, r: RULES.baseSight }];
    for (const o of this.heroes) if (o.team === team && o.alive) out.push({ x: o.pos.x, z: o.pos.z, r: RULES.sight });
    for (const s of this.summons) if (s.team === team && s.alive && birdSight(s)) out.push({ x: s.pos.x, z: s.pos.z, r: birdSight(s), through: true });  // Зоркий глаз
    for (const r of this.reveals) if (r.team === team && r.until > this.t) out.push({ ...r, through: true });
    if (eyeOpen(this, team)) out.push({ x: 0, z: 0, r: Math.hypot(LAYOUT.bounds.x, LAYOUT.bounds.z) * 1.05, through: true });  // Око Бестиария
    return out;
  }
  /* In range and not behind a tree (magic reveals see through). Cached per sim time at quarter-unit
     resolution; many callers per frame. */
  inSight(team: Team, p: { x: number; z: number }): boolean {
    if (eyeOpen(this, team)) return true;  // Око Бестиария: before the cache, which a same-tick cast would leave stale
    if (this.sightT !== this.t) { this.sightT = this.t; this.sightCache.clear(); this.sightCacheStr.clear(); }
    const rx = Math.round(p.x * 4), rz = Math.round(p.z * 4);
    // Packed numeric key while it is exact; the original string key otherwise (NaN, huge coordinates).
    const packed = Math.abs(rx) < SIGHT_KEY_SPAN && Math.abs(rz) < SIGHT_KEY_SPAN;
    const key = packed ? ((rx + SIGHT_KEY_SPAN) * 2 * SIGHT_KEY_SPAN + rz + SIGHT_KEY_SPAN) * 2 + (team === 'a' ? 0 : 1) : 0;
    const strKey = packed ? '' : team + rx + ',' + rz;
    let v = packed ? this.sightCache.get(key) : this.sightCacheStr.get(strKey);
    if (v === undefined) {
      v = this.computeSight(team, p);
      if (packed) this.sightCache.set(key, v); else this.sightCacheStr.set(strKey, v);
    }
    return v;
  }
  /* Same sources, in the same order, as visionSources() — evaluated in place so a cache miss allocates nothing. */
  private computeSight(team: Team, p: { x: number; z: number }, revealOk: (r: Reveal) => boolean = () => true): boolean {
    if (eyeOpen(this, team)) return true;
    const [bx, bz] = LAYOUT.bases[team];
    if (Math.hypot(bx - p.x, bz - p.z) < RULES.baseSight && lineOfSight(bx, bz, p.x, p.z)) return true;
    for (const o of this.heroes) {
      if (o.team === team && o.alive && Math.hypot(o.pos.x - p.x, o.pos.z - p.z) < RULES.sight && lineOfSight(o.pos.x, o.pos.z, p.x, p.z)) return true;
    }
    for (const s of this.summons) {  // Зоркий глаз: a magpie flies over the trees
      if (s.team === team && s.alive && Math.hypot(s.pos.x - p.x, s.pos.z - p.z) < birdSight(s)) return true;
    }
    for (const r of this.reveals) {
      if (r.team === team && r.until > this.t && Math.hypot(r.x - p.x, r.z - p.z) < r.r && revealOk(r)) return true;
    }
    return false;
  }
  /* Без отражения: a Прорицание shows Тень only for its first `revealFor` seconds. */
  private revealShows(r: Reveal, h: Hero) { return h.kind !== 'shadow' || this.t - r.t0 < PASSIVES.shadow.revealFor; }
  /* inSight() for this hero's spot: the cached per-position answer, unless an old reveal Тень shrugs off
     covers her — then recomputed without it. */
  private spotSeen(team: Team, h: Hero): boolean {
    const outlasted = h.kind === 'shadow' && this.reveals.some(r => r.team === team && r.until > this.t && !this.revealShows(r, h)
      && Math.hypot(r.x - h.pos.x, r.z - h.pos.z) < r.r);
    return outlasted ? this.computeSight(team, h.pos, r => this.revealShows(r, h)) : this.inSight(team, h.pos);
  }
  /* Слиться holds while she stands in grass or where the enemy has no sight of her. */
  inCover(h: Hero): boolean {
    return inBrush(h.pos.x, h.pos.z) || !this.spotSeen(h.team === 'a' ? 'b' : 'a', h);
  }
  stealthed(h: Hero) { return h.alive && this.t < h.stealthUntil; }
  /* Без отражения: Тень that has stood `stillFor` s (no step, attack or cast of her own) is a silhouette — seen by
     all, but no attack, skill, zone or CC can touch her. */
  silhouette(h: Hero) { return h.kind === 'shadow' && h.alive && this.t - h.stillSince >= PASSIVES.shadow.stillFor; }
  /* Her own attack or cast ends it like a step does, and the count starts over. Тень only: Сошка counts steps alone. */
  stir(h: Hero) { if (h.kind === 'shadow') h.stillSince = this.t; }
  /* Fog of war, then brush: an enemy in tall grass stays hidden even in sight, unless revealed or approached.
     Слиться beats all of it, Звездочёт included — only Око Бестиария beats Слиться. */
  visibleTo(team: Team, h: Hero): boolean {
    if (h.team === team || eyeOpen(this, team)) return true;
    if (!h.alive) return this.inSight(team, h.pos);
    if (this.t < h.stealthUntil) return false;
    if (this.t < h.revealedTo[team]) return true;
    if (!this.spotSeen(team, h)) return false;
    if (!inBrush(h.pos.x, h.pos.z) || this.t < h.revealUntil) return true;
    if (this.reveals.some(r => r.team === team && r.until > this.t && Math.hypot(r.x - h.pos.x, r.z - h.pos.z) < r.r && this.revealShows(r, h))) return true;
    if (this.pools.some(z => z.reveal && z.team === team && flatDist(z.pos, h.pos) < z.r)) return true;
    return this.heroes.some(o => o.team === team && o.alive && flatDist(o.pos, h.pos) < RULES.brushSight);
  }
  /* A totem is not stealthed like a trap — fog and brush rules apply as for a hero: in tall grass the enemy
     sees it only through a reveal or up close, so a totem never gives away a hidden hero's spot. */
  totemVisibleTo(team: Team, b: { team: Team; pos: THREE.Vector3 }): boolean {
    if (b.team === team || eyeOpen(this, team)) return true;
    if (!this.inSight(team, b.pos)) return false;
    if (!inBrush(b.pos.x, b.pos.z)) return true;
    const p = b.pos;
    return this.reveals.some(r => r.team === team && r.until > this.t && Math.hypot(r.x - p.x, r.z - p.z) < r.r)
      || this.pools.some(z => z.reveal && z.team === team && flatDist(z.pos, p) < z.r)
      || this.heroes.some(o => o.team === team && o.alive && flatDist(o.pos, p) < RULES.brushSight);
  }
  targetVisibleTo(team: Team, t: Target): boolean {
    return isMonster(t) ? monsterVisibleTo(this, team, t) : isSummon(t) ? summonVisibleTo(this, team, t) : this.visibleTo(team, t);
  }
  summonById(id: number): Summon | undefined { return this.summons.find(s => s.id === id && s.alive); }

  /* ---------------- orders (input, bots and Command.apply share these) ---------------- */
  taunted(h: Hero) { return !!(h.taunt && h.taunt.until > this.t && h.taunt.by.alive); }
  forcedKind(h: Hero): Forced['kind'] | null { return h.forced && h.forced.until > this.t ? h.forced.kind : null; }
  /* Соблазн / Стая's fear. CC like a stun: Святой круг and a dodge (Финт, Плащ, Стая's own flight) stop it,
     Смирение shortens it. Replaces any earlier charm/fear. */
  force(target: Hero, by: Hero, kind: Forced['kind'], dur: number, drain?: number): boolean {
    if (!target.alive || this.protectedBy(target) || this.t < target.invulnUntil || this.silhouette(target)) return false;
    dur = this.ccFor(target, dur, this.forcedKind(target) ? (target.forced as Forced).until : this.t);
    target.forced = { kind, by, until: this.t + dur, drain, drainAt: this.t };  // first draw at once: life/DRAIN_TICK draws in all
    target.order = null; target.path = []; target.repathAt = 0;
    target.pending = null; target.pendingTotem = null;
    if (target.state !== 'normal') this.interrupt(target);
    this.emit({ type: 'fx', name: kind, hero: target.id });
    return true;
  }
  taunt(target: Hero, by: Hero, dur: number) {
    target.taunt = { by, until: this.t + dur };
    target.order = { type: 'attack', target: by };
    target.repathAt = 0;
    if (target.state === 'charging' || target.state === 'channel' || target.state === 'casting') this.interrupt(target);
    this.emit({ type: 'fx', name: 'taunt', hero: target.id });
  }
  /* `fx` false: the caller's own event draws the buff (Сделка, Облик Волчицы, Ветряное масло) — a second,
     generic one would stack over it. */
  buff(h: Hero, type: 'haste' | 'dmg', val: number, dur: number, fx = true) {
    h.buffs.push({ type, val, until: this.t + dur });
    if (fx) this.emit({ type: 'fx', name: 'buff', hero: h.id, buffType: type });
  }
  buffVal(h: Hero, type: 'haste' | 'dmg') { return h.buffs.filter(b => b.type === type && b.until > this.t).reduce((m, b) => Math.max(m, b.val), 0); }
  protectedBy(h: Hero) { return this.pools.some(z => z.immune && z.team === h.team && flatDist(z.pos, h.pos) < z.r); }

  orderMove(h: Hero, x: number, z: number) {
    if (!h.alive || this.taunted(h) || this.forcedKind(h)) return;
    h.order = { type: 'move' };
    h.path = this.nav.findPath(h.pos.x, h.pos.z, x, z);
  }
  orderAttack(h: Hero, target: Target) {
    if (!h.alive || !target.alive || (!isMonster(target) && target.team === h.team) || (isHero(target) && this.silhouette(target)) || this.taunted(h) || this.forcedKind(h)) return;
    h.order = { type: 'attack', target };
    h.repathAt = 0;
  }
  orderAttackTotem(h: Hero, totem: Totem) {
    if (!h.alive || totem.team === h.team || this.taunted(h) || this.forcedKind(h)) return;
    h.order = { type: 'attackTotem', target: totem.id };
    h.repathAt = 0;
  }
  orderFollow(h: Hero, ally: Hero) {
    if (!h.alive || ally === h || ally.team !== h.team || this.taunted(h) || this.forcedKind(h)) return;
    h.order = { type: 'follow', target: ally };
    h.repathAt = 0;
  }
  /* "stop" means stop: also drops a swing already queued (h.pending), not just the standing order. */
  stop(h: Hero) {
    if (!h.alive || this.taunted(h) || this.forcedKind(h)) return;
    h.order = null;
    h.path = [];
    h.pending = null;
    h.pendingTotem = null;
  }
  canCast(h: Hero, key: SkillKey) {
    const sk = SKILLS[h.kind]?.[key];
    return !!sk && h.alive && h.cd[key] <= 0 && this.t >= h.stunUntil && h.state === 'normal' && this.t >= h.cloakUntil && !this.taunted(h) && !h.pull
      && !this.forcedKind(h) && (!('cover' in sk) || this.inCover(h));
  }
  castSkill(h: Hero, key: SkillKey, target: Hero | Vec2 | null) { return castSkill(this, h, key, target); }
  canCastArtifact(h: Hero, slot: ArtifactSlotKey) { return canCastArtifact(this, h, slot); }
  castArtifact(h: Hero, slot: ArtifactSlotKey, target: Target | Vec2 | null) { return castArtifact(this, h, slot, target); }
  buyArtifact(h: Hero, slot: ArtifactSlotKey, line: ArtifactLine) { return buyArtifact(this, h, slot, line); }
  sellArtifact(h: Hero, slot: ArtifactSlotKey) { return sellArtifact(this, h, slot); }
  strikeTotem(id: number, by: Hero) { return strikeTotem(this, id, by); }
  canCastRelic(h: Hero) { return canCastRelic(this, h); }
  castRelic(h: Hero) { return castRelic(this, h); }
  buyRelic(h: Hero, line: RelicLine) { return buyRelic(this, h, line); }
  eyeOpen(team: Team) { return eyeOpen(this, team); }
  setState(h: Hero, s: HeroState) { h.state = s; h.stateT = 0; }

  /* ---------------- commands: the wire-safe surface network input drives ---------------- */
  /* A modified client must never be able to ask for more than the real one could: input.js only ever
     reaches orderFollow/castSkill with a hero id after unitAt() has filtered it through visibleTo(), so
     a hero-id target here is resolved to its live position only under that same gate — otherwise it is
     dropped (an attack order self-corrects every tick already, see updateHero, so it needs no gate here). */
  apply(heroId: HeroId, cmd: Command) {
    const h = this.heroes[heroId];
    if (!h) return;
    switch (cmd.type) {
      case 'move': this.orderMove(h, cmd.x, cmd.z); return;
      case 'attack': {
        const at = cmd.target;
        if ('hero' in at) { const t = this.heroes[at.hero]; if (t) this.orderAttack(h, t); }
        else if ('monster' in at) { const m = this.monsters[at.monster]; if (m && monsterVisibleTo(this, h.team, m)) this.orderAttack(h, m); }
        else if ('summon' in at) { const s = this.summonById(at.summon); if (s && summonVisibleTo(this, h.team, s)) this.orderAttack(h, s); }
        else { const b = this.totems.find(x => x.id === at.totem); if (b && this.totemVisibleTo(h.team, b)) this.orderAttackTotem(h, b); }
        return;
      }
      case 'follow': {
        const t = this.heroes[cmd.target];
        if (t && t.team === h.team && this.visibleTo(h.team, t)) this.orderFollow(h, t);
        return;
      }
      case 'cast': {
        if (cmd.key === 'T') { this.castRelic(h); return; }  // both relic actives are instant: no target
        const skill = cmd.key === 'Q' || cmd.key === 'W';
        let target: Target | THREE.Vector3 | null = null;
        if (typeof cmd.target === 'number') {
          const t = this.heroes[cmd.target];
          if (t && this.visibleTo(h.team, t)) target = t;
        } else if (cmd.target && 'monster' in cmd.target) {
          // Same gate as an attack on a monster: only one this team can see.
          const m = this.monsters[cmd.target.monster];
          if (m && monsterVisibleTo(this, h.team, m)) target = skill ? v3(m.pos.x, m.pos.z) : m;
        } else if (cmd.target && 'summon' in cmd.target) {
          const s = this.summonById(cmd.target.summon);
          if (s && summonVisibleTo(this, h.team, s)) target = skill ? v3(s.pos.x, s.pos.z) : s;
        } else if (cmd.target) {
          target = v3(cmd.target.x, cmd.target.z);
        }
        if (skill) this.castSkill(h, cmd.key as SkillKey, target as Hero | THREE.Vector3 | null);
        else this.castArtifact(h, cmd.key as ArtifactSlotKey, target);
        return;
      }
      case 'aim': aimCharge(this, h, v3(cmd.x, cmd.z)); return;
      case 'release': releaseCharge(this, h); return;
      case 'stop': this.stop(h); return;
      case 'buyArtifact': this.buyArtifact(h, cmd.slot, cmd.line); return;
      case 'sellArtifact': this.sellArtifact(h, cmd.slot); return;
      case 'buyRelic': this.buyRelic(h, cmd.line); return;
    }
  }

  /* ---------------- combat ---------------- */
  speedOf(h: Hero) {
    const slow = Math.max(0, ...h.slows.filter(s => s.until > this.t).map(s => s.pct));
    const self = h.state === 'brace' ? SKILLS.rhino.W.slow : h.state === 'charging' ? SKILLS.arquebusier.Q.slow : 0;
    // Сапоги гонца sums in alongside haste, the same additive shape haste already uses. The ground (a ford)
    // multiplies like any other slow: it stacks with the strongest status slow instead of competing with it.
    return h.def.speed * (1 - slow) * (1 - self) * this.groundSpeed(h.pos.x, h.pos.z) * (1 + this.buffVal(h, 'haste') + passiveMoveSpeedBonusOf(h, this.t) + relicMoveSpeedBonusOf(this, h));
  }

  dmgTakenMultOf(t: Target) { return 1 + t.marks.filter(m => m.until > this.t).reduce((sum, m) => sum + m.pct, 0); }
  // No team check: "лечение внутри" in the doc carries no team qualifier — the caster's own team is
  // reduced too if they stand in their own puddle.
  healReductionOf(target: Hero) {
    return this.pools.reduce((m, z) => (z.healReduction && flatDist(z.pos, target.pos) < z.r ? Math.max(m, z.healReduction) : m), 0);
  }

  /* Pipeline: dodge (Финт/Плащ) → shield wall (Стойка) → Покровитель → ward (Оберег) → health. A monster
     target takes only the attacker's own damage buffs, Memento mori's bonus never (heroes only) and Клеймо,
     none of the rest (monsters.ts damageMonster). `fromPos`, when given, is where the hit comes from instead
     of `src.pos` (a monster's swing, a totem's pulse, a confused ally's swing) — Стойка blocks it from the front. `basic`: a hero's basic attack
     (Мерзость reads it); `burn`: Саламандра's fire, which ignites Липкое пламя when it connects. `by`: whose
     unit the `hit` fx names when that is not `src` itself (a summon's or a monster's swing, a confused ally's),
     null for none. */
  damage(target: Target, amount: number, src: Hero | null, opts: { stun?: number; knock?: THREE.Vector3 | null; slow?: number; slowFor?: number; skill?: boolean; skipSrcBuff?: boolean; skipTargetMult?: boolean; fromPos?: { x: number; z: number }; basic?: boolean; burn?: boolean; shared?: boolean; by?: Target | null } = {}) {
    let { stun = 0, knock = null, slow = 0, slowFor = 0, skill = false, skipSrcBuff = false, skipTargetMult = false } = opts;
    const by = opts.by === undefined ? src : opts.by;
    if (!target.alive) return 0;
    if (isHero(target) && this.silhouette(target)) return 0;  // Без отражения: nothing lands, not even a slow or a push
    if (!skipSrcBuff && src) amount *= Math.max(1, this.buffVal(src, 'dmg'));
    // Memento mori: read against the victim's hp right now, before any mitigation.
    const memento = !opts.shared && !!src && src.kind === 'death' && isHero(target) && target.team !== src.team && target.hp < maxHpOf(target) * PASSIVES.death.below;
    if (memento) amount *= 1 + PASSIVES.death.bonus;
    if (!opts.shared) amount *= dragonToothMultOf(src, target);  // Зуб дракона: same before-mitigation hp read
    if (!opts.shared) amount *= eyeDmgMultOf(this, src, target);  // Око Бестиария
    if (!skipTargetMult) amount *= this.dmgTakenMultOf(target);  // Клеймо
    if (isMonster(target)) {
      const hpBefore = target.hp, dealt = damageMonster(this, target, amount, src, skill, by);
      this.stats.monsterDamaged(src ? src.id : null, Math.min(dealt, Math.max(0, hpBefore)));
      return dealt;
    }
    if (isSummon(target)) return damageSummon(this, target, amount, src, skill, by);
    if (this.protectedBy(target)) { stun = 0; slow = 0; knock = null; }  // Святой круг
    if (this.t < target.invulnUntil) {
      // Финт only arms on its own 'feint' state: Плащ also sets invulnUntil, and must not give Смерть a
      // free empowered Выпад just because someone swung at a cloaked hero.
      if (target.state === 'feint' && target.kind === 'death' && !target.feintArmed) { target.feintArmed = true; this.emit({ type: 'fx', name: 'feintArmed', hero: target.id }); }
      return 0;
    }
    // A hit that actually connects (past dodge/invuln above) counts as "in combat" regardless of how much
    // of it lands numerically — Целебная мазь's own out-of-combat gate reads this, and this single hook
    // covers a direct hit and a zone/DOT tick alike (both call damage(), `tick` only changes what else
    // reads it), closing the gap a per-call-site flag could otherwise leave open.
    target.lastDamageAt = this.t;
    const from = opts.fromPos ?? src?.pos;
    if (target.state === 'brace' && from) {
      const toSrc = Math.atan2(from.x - target.pos.x, from.z - target.pos.z);
      if (Math.abs(angDiff(target.wantYaw, toSrc)) < SKILLS.rhino.W.arc) {
        target.braceArmed = true;
        this.emit({ type: 'fx', name: 'block', pos: this.pos2(target.pos), yaw: target.wantYaw });
        return 0;
      }
    }
    const hostile = !!src && src.team !== target.team;
    // The attacker's on-hit passives belong to the hit he landed, not to Расплата's share Грех takes of it.
    const onHit = hostile && !opts.shared;
    if (onHit && src!.kind === 'astrologer') this.revealTo(target, src!);
    if (onHit && opts.burn) target.burn = { src: src!, ticks: PASSIVES.salamander.ticks, tickAt: this.t + PASSIVES.salamander.interval };
    if (onHit && src!.kind === 'sin') target.sinHitAt = this.t;  // Первородный
    let dmg = amount;
    // Расплата: Грех takes his share of it through his own pipeline (his own shield, dodge, Покровитель).
    const pact = !opts.shared && (!src || hostile) ? this.atonementFor(target) : undefined;
    if (pact) {
      // Расплата: the ally is spared `share` of the hit; Грех himself takes only `taken` of it (the rest is lost).
      const hit = dmg;
      dmg -= hit * (pact.share as number);
      this.damage(pact.src, hit * ((pact.taken ?? pact.share) as number), src, { skipSrcBuff: true, skipTargetMult: true, fromPos: from, skill, shared: true, by });
    }
    const guard = this.guardianOf(target);
    if (guard) {
      const saved = dmg * PASSIVES.durer.reduce;
      dmg -= saved;
      this.stats.passive(guard.id, saved);
    }
    const W = PASSIVES.shewolf;  // Вожак
    if (target.kind === 'shewolf' && this.summons.some(s => s.alive && s.owner === target && s.kind === 'wolf' && flatDist(s.pos, target.pos) <= W.r)) {
      const saved = dmg * W.reduce;
      dmg -= saved;
      this.stats.passive(target.id, saved);
    }
    if (target.shield && target.shield.until > this.t) {
      const soak = Math.min(target.shield.amount, dmg);
      target.shield.amount -= soak;
      dmg -= soak;
      if (target.shield.amount <= 0) { target.shield = null; this.emit({ type: 'fx', name: 'wardBreak', hero: target.id }); }
    }
    const dealt = Math.min(dmg, Math.max(0, target.hp));
    this.stats.heroDamaged(src ? src.id : null, target.id, dealt, this.t);
    target.hp -= dmg;
    if (memento) this.stats.passive(src!.id, dealt * PASSIVES.death.bonus / (1 + PASSIVES.death.bonus));
    if (onHit && src!.kind === 'devil' && src!.alive && dealt > 0) {
      const before = src!.hp;
      this.heal(src!, dealt * PASSIVES.devil.leech);
      this.stats.passive(src!.id, src!.hp - before);
    }
    if (skill && dmg > 0) this.emit({ type: 'num', hero: target.id, value: Math.round(dmg) });
    if (knock) {
      if (target.kind === 'rhino') {
        knock = knock.clone().multiplyScalar(PASSIVES.rhino.mult);
        this.stats.passive(target.id, knock.length() / KB_DECAY);  // a slide at speed v decaying at KB_DECAY covers v/KB_DECAY
      }
      target.kb.add(knock);
    }
    if (slow) {
      slowFor = this.ccFor(target, slowFor, this.slowEndOf(target));
      target.slows.push({ pct: slow, until: this.t + slowFor });
      if (opts.basic && src && src.kind === 'ferryman') this.stats.passive(src.id, slowFor);
    }
    if (stun) {
      stun = this.ccFor(target, stun, target.stunUntil);
      target.stunUntil = Math.max(target.stunUntil, this.t + stun);
      target.pending = null;
      target.pendingTotem = null;
      if (target.state !== 'normal') this.interrupt(target);
    }
    this.emitHit({ type: 'fx', name: 'hit', pos: this.pos2(target.pos), color: src && src.def.bolt ? src.def.bolt : (src && src.team === 'a' ? 0xffb347 : 0x7fe0ff), big: skill }, target, by);
    // Мерзость: fires on the connecting blow even when it is the lethal one.
    if (opts.basic && hostile && target.kind === 'grotesque' && this.meleeOf(src!)) {
      this.stats.passive(target.id, this.passiveHit(src!, PASSIVES.grotesque.dmg, target, { skill: true, skipSrcBuff: true }));
    }
    if (target.hp <= 0) { this.kill(target, src); return dmg; }
    if (target.state === 'normal') this.emitAnim(target.id, ['Hit'], 0.12, { skipIfBusy: true });
    return dmg;
  }

  /* damage() for a passive's own extra hit: returns the hp it actually removed, for that passive's stat. */
  private passiveHit(target: Target, amount: number, src: Hero, opts: Parameters<Game['damage']>[3] = {}) {
    const before = Math.max(0, target.hp);
    this.damage(target, amount, src, opts);
    return before - Math.max(0, target.hp);
  }

  /* Расплата: an allied Грех's zone covering this hero (never Грех himself). */
  atonementFor(t: Hero): Zone | undefined {
    return this.pools.find(z => z.share && z.team === t.team && z.src !== t && z.src.alive && z.until > this.t && flatDist(z.pos, t.pos) <= z.r);
  }
  /* Наваждение: the Кошмар whose zone this hero stands in, if any. */
  confusedBy(h: Hero): Hero | undefined {
    return this.pools.find(z => z.confuse && z.team !== h.team && z.until > this.t && flatDist(z.pos, h.pos) <= z.r)?.src;
  }
  /* Бессонница: a living enemy Кошмар close enough to deny this hero's healing. */
  sleeplessBy(h: Hero): Hero | undefined {
    return this.heroes.find(n => n.kind === 'nightmare' && n.alive && n.team !== h.team && flatDist(n.pos, h.pos) <= PASSIVES.nightmare.r);
  }

  /* Покровитель: a living allied Дюрер (not the target itself) close enough to cover it. */
  guardianOf(t: Hero): Hero | undefined {
    return this.heroes.find(d => d.kind === 'durer' && d !== t && d.alive && d.team === t.team && flatDist(d.pos, t.pos) <= PASSIVES.durer.r);
  }

  /* Смирение: a stun or slow landing on the monk lasts shorter. `end`: when that kind of CC on him ends
     already — the stat counts only CC time actually cut, not overlap that changed nothing. */
  ccFor(h: Hero, secs: number, end: number) {
    if (h.kind !== 'monk') return secs;
    const short = secs * PASSIVES.monk.mult;
    this.stats.passive(h.id, Math.max(0, this.t + secs - Math.max(end, this.t + short)));
    return short;
  }
  private slowEndOf(h: Hero) { return h.slows.reduce((m, s) => Math.max(m, s.until), this.t); }

  /* Звездочёт: only the astrologer's own team gains sight of the victim — see visibleTo(). */
  private revealTo(target: Hero, by: Hero) {
    const until = this.t + PASSIVES.astrologer.reveal, prev = target.revealedTo[by.team];
    if (until <= prev) return;
    this.stats.passive(by.id, until - Math.max(prev, this.t));
    target.revealedTo[by.team] = until;
  }

  /* Basic-attack reach; Сошка lengthens it once the arquebusier has stood still long enough, Обернуться makes
     it a wolf's bite. */
  attackRangeOf(h: Hero) {
    if (this.wolfForm(h)) return SKILLS.shewolf.W.range;
    const P = PASSIVES.arquebusier;
    return h.def.range + (h.kind === 'arquebusier' && this.t - h.stillSince >= P.still ? P.bonus : 0);
  }
  wolfForm(h: Hero) { return h.alive && this.t < h.wolfUntil; }
  /* Swings rather than shoots: a melee hero, or Волчица in wolf form. */
  meleeOf(h: Hero) { return !!h.def.melee || this.wolfForm(h); }


  /* `src`: the hero whose skill this heal is, for the match stats — omitted for fountain, regen and potions.
     Бессонница denies every heal but the fountain's, which it halves. */
  heal(target: Hero, amount: number, src?: Hero, fountain = false) {
    if (!target.alive || amount <= 0) return;
    const denier = this.sleeplessBy(target);
    if (denier) {
      const cut = fountain ? amount * (1 - PASSIVES.nightmare.fountain) : amount;
      this.stats.passive(denier.id, Math.min(cut, maxHpOf(target) - target.hp));
      amount -= cut;
      if (amount <= 0) return;
    }
    const before = target.hp;
    target.hp = Math.min(maxHpOf(target), target.hp + amount * (1 - this.healReductionOf(target)));  // Смоляная бутыль
    if (src) this.stats.healed(src.id, target.hp - before);
    if (target.hp - before >= 1) this.emit({ type: 'num', hero: target.id, value: Math.round(target.hp - before), heal: true });
  }

  interrupt(h: Hero) {
    h.dash = null; h.hop = null; h.charge = null; h.cast = null; h.channel = null; h.lift = 0;
    this.setState(h, 'normal');
  }

  /* Knockback into a pillar, wall, tree or rock: the Таран's reward for fighting around cover. Takes a
     plain point (not a Hero) so the caller can pass the same rewound position the hit itself was decided
     against — the slam bonus must be resolved in the same frame of reference as the hit that triggers it. */
  wallBehind(victim: { x: number; z: number }, dir: THREE.Vector3, dist: number) {
    for (let k = 0.5; k <= dist; k += 0.4) {
      if (solid(victim.x + dir.x * k, victim.z + dir.z * k)) return true;
    }
    return false;
  }

  kill(h: Hero, killer: Hero | null = null) {
    h.alive = false;
    h.hp = 0;
    h.marks = []; h.cloakUntil = 0;
    h.order = null; h.path = []; h.pending = null; h.pendingTotem = null; h.shield = null; h.slows = []; h.buffs = []; h.hots = []; h.taunt = null; h.pull = null; h.burn = null;
    h.stealthUntil = 0; h.ambushLeft = 0; h.forced = null; h.wallShadow = null;
    h.wolfUntil = 0; h.tether = null;
    this.interrupt(h);
    h.diedAt = this.t;
    h.respawnAt = this.t + Math.min(RULES.respawnMax, RULES.respawnBase + RULES.respawnPerMinute * Math.floor(this.clock / 60)) * respawnMultOf(h);
    this.emitAnim(h.id, ['Death'], 0.1);
    this.emit({ type: 'fx', name: 'splat', pos: this.pos2(h.pos), big: true });
    this.emit({ type: 'fx', name: 'death', pos: this.pos2(h.pos) });
    this.stats.died(h.id, killer ? killer.id : null, this.t, killer ? killer.alive : true);
    loseRelicOnDeath(this, h);
    if (killer) {
      this.score[killer.team] += RULES.killScore;  // counts even if the killer died first (a DoT, a pool)
      const sin = this.t - h.sinHitAt <= PASSIVES.sin.window ? this.heroes.find(s => s.kind === 'sin' && s.team === killer.team) : undefined;
      if (sin) { this.score[killer.team] += PASSIVES.sin.bonus; this.stats.passive(sin.id, PASSIVES.sin.bonus); }  // Первородный
    }
    h.sinHitAt = -Infinity;
  }

  respawn(h: Hero) {
    h.alive = true;
    h.hp = maxHpOf(h);
    h.lastDamageAt = -Infinity;  // a fresh spawn is never "recently hit" — Целебная мазь can tick right away
    h.cd.Q = h.cd.W = 0;
    // Artefact cooldowns do NOT reset on respawn (unlike Q/W): a 6s death must not be a free reset on a
    // 45s Клеймо or similar.
    // A normal post-death respawn already went through kill() → interrupt(), so this is a no-op there;
    // it only matters when reset() calls respawn() directly on a hero who was alive and mid-skill (a
    // match ending while someone is channeling, dashing, cloaked, etc.) — that state must not survive.
    this.interrupt(h);
    h.invulnUntil = 0;
    h.cloakUntil = 0;
    h.pending = null;
    h.pendingTotem = null;
    h.kb.set(0, 0, 0);
    h.pull = null;  // a hook mid-drag at reset would otherwise lerp the fresh spawn to the old drag target
    h.stunUntil = 0;
    h.feintArmed = h.empowered = h.braceArmed = false;
    h.burn = null;
    h.stealthUntil = 0; h.ambushLeft = 0; h.forced = null; h.wallShadow = null; h.sinHitAt = -Infinity;
    h.wolfUntil = 0; h.tether = null;
    h.stillSince = this.t;
    this.placeAtBase(h, h.id);
    this.emitAnim(h.id, ['Idle'], 0);
  }

  /* Точильный камень, Грифон and Рог Дикой Охоты, all additive fractions of the base attack rate. */
  attackCdOf(h: Hero) { return h.def.attackCd / (attackSpeedMultOf(h) + relicAtkSpeedBonusOf(this, h)); }

  startAttack(h: Hero, target: Target) {
    h.cd.attack = this.attackCdOf(h);
    this.stir(h);
    h.revealUntil = this.t + RULES.revealAfterAttack;
    // Слиться: attacking out of it ends it and arms `ambushHits` empowered swings, spent within `ambushWindow`.
    const Q = SKILLS.shadow.Q;
    if (this.stealthed(h)) { h.ambushLeft = Q.ambushHits; h.ambushUntil = this.t + Q.ambushWindow; }
    h.stealthUntil = 0;
    const ambush = h.ambushLeft > 0 && this.t < h.ambushUntil;
    if (ambush) h.ambushLeft--; else h.ambushLeft = 0;
    h.wantYaw = yawTo(h.pos, target.pos);
    h.swingAt = this.t;
    const clips = h.def.clips;
    if (clips.length) this.emitAnim(h.id, [clips[h.clipFlip++ % clips.length] as string], 0.06);
    h.pending = { at: this.t + h.def.impact, target, ...(ambush ? { mult: SKILLS.shadow.Q.ambush } : {}) };
  }

  /* A basic attack ordered at an enemy totem instead of a hero — same attackCd/impact rhythm, but only
     strikeTotem() (one of its 2 hits) resolves the hit, never the damage() pipeline or a skillshot. */
  startAttackTotem(h: Hero, totem: Totem) {
    h.cd.attack = this.attackCdOf(h);
    this.stir(h);
    h.stealthUntil = 0; h.ambushLeft = 0;  // Слиться ends on any basic attack; a totem takes its two hits whatever the damage, so the ambush is spent
    h.wantYaw = yawTo(h.pos, totem.pos);
    h.swingAt = this.t;
    const clips = h.def.clips;
    if (clips.length) this.emitAnim(h.id, [clips[h.clipFlip++ % clips.length] as string], 0.06);
    h.pendingTotem = { at: this.t + h.def.impact, totemId: totem.id };
  }

  resolveAttackTotem(h: Hero) {
    const { totemId } = h.pendingTotem as PendingTotem;
    h.pendingTotem = null;
    const totem = this.totems.find(b => b.id === totemId);
    if (!totem) return;  // destroyed or expired before the swing landed
    if (this.meleeOf(h)) {
      if (flatDist(h.pos, totem.pos) > this.attackRangeOf(h) + 0.9) return;
      this.emit({ type: 'fx', name: 'slash', hero: h.id, flip: h.clipFlip % 2 });
      this.strikeTotem(totem.id, h);
    } else {
      // Mirrors the hero path exactly: a real travelling bolt, not a hitscan — it can be blocked by a
      // Павеза barrier, and the totem only loses a hit point once the bolt actually arrives.
      this.emit({ type: 'fx', name: 'muzzle', hero: h.id });
      this.spawnHomingTotem(h, totem);
    }
  }

  resolveAttack(h: Hero) {
    const { target, mult = 1 } = h.pending as Pending;
    h.pending = null;
    if (!target.alive || (isHero(target) && this.silhouette(target))) return;  // she stilled mid-swing
    if (this.confusedSwing(h, mult)) return;
    if (this.meleeOf(h)) {
      const seen = this.rewind(h, target);
      if (!seen.alive || flatDist(h.pos, seen) > this.attackRangeOf(h) + 0.9 + bulkOf(target)) return;
      if (this.basicMissed(target)) {  // a whiff spends the swing (and Стойка's empowered hit), lands nothing
        h.empowered = false;
        this.emit({ type: 'fx', name: 'slash', hero: h.id, flip: h.clipFlip % 2 });
        return;
      }
      // Упор's charged hit lands as a skill hit (stunning), so its `hit` is `big`: the rhino's impact, scaled up, is its picture
      const stun = h.empowered ? SKILLS.rhino.W.empoweredStun : 0;
      h.empowered = false;
      this.emit({ type: 'fx', name: 'slash', hero: h.id, flip: h.clipFlip % 2 });
      const heavy = h.kind === 'ferryman' ? { slow: PASSIVES.ferryman.slow, slowFor: PASSIVES.ferryman.slowFor } : {};  // Тяжёлая рука
      this.damage(target, h.def.dmg * mult, h, { stun, skill: stun > 0, basic: true, ...heavy });
      if (h.kind === 'landsknecht' && h.alive) this.cleave(h, target, seen);  // not if Мерзость just killed him
    } else {
      this.emit({ type: 'fx', name: 'muzzle', hero: h.id });
      this.spawnHoming(h, target, mult);
    }
  }

  /* Туманный амулет: rolled on the basic attack's FINAL target, once it is about to land (a melee swing
     connecting, a bolt arriving; after Наваждение has redirected it). A miss shows «промах» and does nothing
     else — no damage, so no on-hit effects either. */
  private basicMissed(target: Target): boolean {
    const chance = basicMissChanceOf(target);
    if (!chance || this.missRng() >= chance) return false;
    this.emit({ type: 'num', hero: (target as Hero).id, value: 0, miss: true });
    return true;
  }

  /* Наваждение: a confused hero's basic attack may land on its nearest ally in reach instead, weakened, and
     credited to the Кошмар (his damage, his kill). No ally in reach: an ordinary attack. */
  private confusedSwing(h: Hero, mult: number): boolean {
    const by = this.confusedBy(h);
    if (!by) return false;
    const reach = this.attackRangeOf(h) + (this.meleeOf(h) ? 0.9 : 0);
    let ally: Hero | undefined, best = Infinity;
    for (const a of this.heroes) {
      const d = flatDist(a.pos, h.pos);
      if (a !== h && a.alive && a.team === h.team && d <= reach && d < best && !this.silhouette(a)) { ally = a; best = d; }
    }
    const sk = SKILLS.nightmare.W;
    if (!ally || this.confuseRng() >= sk.chance) return false;
    const dmg = h.def.dmg * mult * sk.mult * Math.max(1, this.buffVal(h, 'dmg'));
    h.wantYaw = yawTo(h.pos, ally.pos);
    if (this.meleeOf(h)) {
      this.emit({ type: 'fx', name: 'slash', hero: h.id, flip: h.clipFlip % 2 });
      if (!this.basicMissed(ally)) this.damage(ally, dmg, by, { skipSrcBuff: true, fromPos: h.pos, by: h });
    } else {
      this.emit({ type: 'fx', name: 'muzzle', hero: h.id });
      this.projectiles.push({ kind: 'homing', src: h, blame: by, target: ally, pos: new THREE.Vector3(h.pos.x, 1.5, h.pos.z), speed: h.def.boltSpeed || 18, dmg, color: h.def.bolt, live: true });
    }
    return true;
  }

  /* Двуручник: the same swing also catches the nearest other hostile in reach within the arc toward the
     main target (`at`, where the main hit was decided). Ties keep hostilesOf() order — heroes first. */
  private cleave(h: Hero, main: Target, at: { x: number; z: number }) {
    const P = PASSIVES.landsknecht, fwd = yawTo(h.pos, at);
    let best: Target | undefined, bestD = Infinity;
    for (const e of this.hostilesOf(h)) {
      if (e === main) continue;
      const seen = this.rewind(h, e);
      if (!seen.alive) continue;
      const d = flatDist(h.pos, seen);
      if (d > h.def.range + 0.9 + bulkOf(e) || d >= bestD) continue;
      if (d > 1e-3 && Math.abs(angDiff(fwd, yawTo(h.pos, seen))) > P.arc / 2) continue;
      best = e; bestD = d;
    }
    if (best) this.stats.passive(h.id, this.passiveHit(best, h.def.dmg * P.share, h, { basic: true }));
  }

  /* ---------------- projectiles ---------------- */
  spawnHoming(src: Hero, target: Target, mult = 1) {
    this.projectiles.push({ kind: 'homing', src, target, pos: new THREE.Vector3(src.pos.x, 1.5, src.pos.z), speed: src.def.boltSpeed || 18, dmg: src.def.dmg * mult, color: src.def.bolt, live: true });
  }
  spawnHomingTotem(src: Hero, totem: Totem) {
    this.projectiles.push({ kind: 'homing', src, totemId: totem.id, pos: new THREE.Vector3(src.pos.x, 1.5, src.pos.z), speed: src.def.boltSpeed || 18, dmg: 0, color: src.def.bolt, live: true });
  }
  spawnLinear({ src, dir, speed, range, width, dmg, color, big, look, onHit }: { src: Hero; dir: THREE.Vector3; speed: number; range: number; width: number; dmg: number; color?: number; big?: boolean; look?: string; onHit?: (hit: Target) => void }) {
    this.projectiles.push({ kind: 'linear', src, dir: dir.clone(), pos: new THREE.Vector3(src.pos.x, 1.3, src.pos.z), speed, left: range, width, dmg, color, big, look, onHit, live: true });
  }
  spawnLob({ src, to, flight, r, onLand }: { src: Hero; to: THREE.Vector3; flight: number; r: number; onLand: (pt: THREE.Vector3) => void }) {
    this.lobs.push({ src, from: new THREE.Vector3(src.pos.x, 1.6, src.pos.z), to: to.clone(), t0: this.t, flight, r, onLand, live: true });
  }
  /* Павеза: an enemy barrier stops projectiles crossing it. */
  blockedByBarrier(p: Projectile) {
    for (const b of this.barriers) {
      if (b.team === p.src.team || b.until <= this.t) continue;
      const perp = new THREE.Vector3(-b.dir.z, 0, b.dir.x);
      const dx = p.pos.x - b.center.x, dz = p.pos.z - b.center.z;
      if (Math.abs(dx * b.dir.x + dz * b.dir.z) < 0.45 && Math.abs(dx * perp.x + dz * perp.z) < b.half) {
        this.emit({ type: 'fx', name: 'block', pos: this.pos2(p.pos), yaw: Math.atan2(-b.dir.x, -b.dir.z) });
        return true;
      }
    }
    return false;
  }

  private updateProjectiles(dt: number) {
    for (const p of this.projectiles) {
      if (!p.live) continue;
      if (p.kind === 'homing' && p.totemId !== undefined) {
        const totem = this.totems.find(b => b.id === p.totemId);
        if (!totem) { p.live = false; continue; }
        const to = new THREE.Vector3(totem.pos.x, 1.2, totem.pos.z).sub(p.pos);
        const d = to.length(), step = p.speed * dt;
        if (d <= step + 0.3) { p.live = false; this.strikeTotem(totem.id, p.src); }
        else p.pos.addScaledVector(to, step / d);
        if (p.live && this.blockedByBarrier(p)) p.live = false;
      } else if (p.kind === 'homing') {
        // A homing bolt tracks and visually arrives at the target's REAL position every tick — that is
        // what a homing bolt does, and it is what the shooter sees rendered. It already cannot miss
        // geometrically by design, so rewinding its flight would only make it visibly land on empty
        // ground while damage() still lands on the present hero (the bug this fixes). The only fairness
        // question lag compensation can meaningfully answer here is aliveness: was the target already
        // dead, from the shooter's perspective, at the instant the bolt would connect — consulted once,
        // at that instant, discarding the port's position (irrelevant once the bolt has already arrived).
        const target = p.target as Target;
        if (!target.alive) { p.live = false; continue; }
        const to = new THREE.Vector3(target.pos.x, 1.2, target.pos.z).sub(p.pos);
        const d = to.length(), step = p.speed * dt;
        if (p.look === 'falcon') {
          if (p.arcFrom === undefined) p.arcFrom = d;
          p.arc = Math.max(p.arc ?? 0, Math.min(1, 1 - d / p.arcFrom));
        }
        if (d <= step + 0.3) {
          p.live = false;
          // A silhouette takes nothing: the bolt spends itself on her without a miss roll.
          if (!this.rewind(p.src, target).alive || (isHero(target) && this.silhouette(target))) { /* spent on nothing */ }
          else if (p.onHit) p.onHit(target);  // a skill's bolt (Сокол на перчатке): no miss roll, its own effect
          else if (!this.basicMissed(target)) {
            if (p.blame) this.damage(target, p.dmg, p.blame, { skipSrcBuff: true, fromPos: p.src.pos, by: p.src });
            else this.damage(target, p.dmg, p.src);
          }
        } else p.pos.addScaledVector(to, step / d);
        if (p.live && this.blockedByBarrier(p)) p.live = false;
      } else {
        const dir = p.dir as THREE.Vector3;
        const step = Math.min(p.left as number, p.speed * dt);
        p.pos.addScaledVector(dir, step);
        p.left = (p.left as number) - step;
        let hit: Target | undefined, hitSeen: SeenTarget | undefined;
        for (const e of this.hostilesOf(p.src)) {
          const seen = this.rewind(p.src, e);
          if (seen.alive && flatDist(seen, p.pos) < (p.width as number) / 2 + 0.5 + bulkOf(e)) { hit = e; hitSeen = seen; break; }
        }
        if (hit) {
          p.live = false;
          if (p.onHit) p.onHit(hit);
          else {
            this.damage(hit, p.dmg, p.src, { skill: true, knock: dir.clone().multiplyScalar(p.big ? 5 : 2) });
            this.emit({ type: 'fx', name: 'splat', pos: { x: hitSeen!.x, z: hitSeen!.z }, big: p.big }, hit);  // where the shot connected, not the target's present spot
          }
        } else if (this.blockedByBarrier(p)) p.live = false;
        else if ((p.left as number) <= 0.01 || solid(p.pos.x, p.pos.z)) { p.live = false; this.emit({ type: 'fx', name: 'hit', pos: this.pos2(p.pos), color: p.color ?? 0xffffff }); }
      }
    }
    this.projectiles = this.projectiles.filter(p => p.live);
    for (const l of this.lobs) {
      const k = (this.t - l.t0) / l.flight;
      if (k >= 1) { l.live = false; l.onLand(l.to); }
    }
    this.lobs = this.lobs.filter(l => l.live);
  }

  /* A pool's heal pulse; Сортировка makes the plague doctor's 20% stronger on a badly hurt ally. */
  private healTick(h: Hero, amount: number, src: Hero) {
    const P = PASSIVES.plague, boost = src.kind === 'plague' && h.hp < maxHpOf(h) * P.below;
    const before = h.hp;
    this.heal(h, boost ? amount * (1 + P.bonus) : amount, src);
    if (boost) this.stats.passive(src.id, (h.hp - before) * P.bonus / (1 + P.bonus));
  }

  private updateZones(dt: number) {
    for (const z of this.pools) {
      if (z.follow) { if (z.follow.alive) z.pos.copy(z.follow.pos); else z.until = this.t; }
      if (z.vel) {
        const nx = z.pos.x + z.vel.x * dt, nz = z.pos.z + z.vel.z * dt;
        if (solid(nx, nz)) z.vel.set(0, 0, 0); else z.pos.set(nx, 0, nz);
      }
      if (this.t >= z.tickAt) {  // zones pulse twice a second so the numbers stay readable
        z.tickAt = this.t + 0.5;
        for (const h of this.heroes) {
          if (!h.alive || flatDist(h.pos, z.pos) > z.r) continue;
          if (h.team === z.team) { if (z.heal) this.healTick(h, z.heal * 0.5, z.src); }
          else if ((z.dmg || z.slow) && !this.silhouette(h)) {
            if (z.dmg) this.damage(h, z.dmg * 0.5, z.src, { slow: z.slow, slowFor: 0.6, skill: true, burn: z.burn });
            else h.slows.push({ pct: z.slow as number, until: this.t + this.ccFor(h, 0.6, this.slowEndOf(h)) });
          }
        }
        if (z.dmg) {
          for (const m of this.monsters) {
            if (m.alive && flatDist(m.pos, z.pos) <= z.r + bulkOf(m)) this.damage(m, z.dmg * 0.5, z.src, { skill: true });
          }
          for (const s of this.summons) {
            if (s.alive && s.team !== z.team && flatDist(s.pos, z.pos) <= z.r + bulkOf(s)) this.damage(s, z.dmg * 0.5, z.src, { skill: true });
          }
        }
      }
    }
    for (const h of this.heroes) {
      if (!h.alive) continue;
      for (const o of h.hots) {
        if (this.t >= (o.tickAt || 0)) { o.tickAt = this.t + 0.5; this.heal(h, o.perSec * 0.5, o.src); }
      }
      const b = h.burn;
      if (b && this.t >= b.tickAt) {
        const P = PASSIVES.salamander;
        b.tickAt = this.t + P.interval;
        if (--b.ticks <= 0) h.burn = null;
        this.stats.passive(b.src.id, this.passiveHit(h, P.dps * P.interval, b.src, { skill: true }));
      }
      if (!h.alive) continue;
      h.hots = live(h.hots, this.t);
      h.marks = live(h.marks, this.t);
      h.buffs = live(h.buffs, this.t);
      if (h.taunt && !this.taunted(h)) h.taunt = null;
    }
    for (const r of this.reveals) {  // Прорицание: enemies inside lose a share of max hp per second
      if (!r.hpPerSec || !r.src || this.t < (r.tickAt || 0)) continue;
      r.tickAt = this.t + 0.5;
      for (const h of this.heroes) {
        if (h.alive && h.team !== r.team && Math.hypot(r.x - h.pos.x, r.z - h.pos.z) < r.r)
          this.damage(h, maxHpOf(h) * r.hpPerSec * 0.5, r.src, { skill: true });
      }
      for (const m of this.monsters) {
        if (m.alive && Math.hypot(r.x - m.pos.x, r.z - m.pos.z) < r.r + bulkOf(m))
          this.damage(m, MONSTERS[m.kind].hp * r.hpPerSec * 0.5, r.src, { skill: true });
      }
      for (const s of this.summons) {
        if (s.alive && s.team !== r.team && Math.hypot(r.x - s.pos.x, r.z - s.pos.z) < r.r + bulkOf(s))
          this.damage(s, s.maxHp * r.hpPerSec * 0.5, r.src, { skill: true });
      }
    }
    this.barriers = live(this.barriers, this.t);
    this.reveals = live(this.reveals, this.t);
    updateTotems(this);
    this.pools = this.pools.filter(z => z.until > this.t);
    for (const tr of this.traps) {
      if (tr.fired || this.t < tr.armAt) continue;
      const victim: Target | undefined = this.heroes.find(h => h.alive && h.team !== tr.team && flatDist(h.pos, tr.pos) < tr.r && !this.silhouette(h))
        ?? this.monsters.find(m => m.alive && flatDist(m.pos, tr.pos) < tr.r + bulkOf(m));
      if (victim) {
        tr.fired = true;
        this.damage(victim, tr.dmg, tr.src, { slow: tr.slow, slowFor: tr.slowFor, skill: true });
        if (isHero(victim)) {
          if (victim.kind === 'shadow') this.stats.passive(victim.id, 2);  // Без отражения: the trap gives her away to no one
          else victim.revealUntil = this.t + 2;
        }
        this.emit({ type: 'fx', name: 'trapPop', pos: this.pos2(tr.pos) }, victim);
      }
    }
    this.traps = this.traps.filter(tr => !tr.fired && tr.until > this.t);
    for (const h of this.heroes) {
      if (h.shield && h.shield.until <= this.t) {  // an unbroken ward refunds part of its cooldown
        const src = h.shield.src;
        if (src && src.alive) src.cd.W = Math.max(0, src.cd.W - SKILLS.plague.W.cd * SKILLS.plague.W.refund);
        h.shield = null;
      }
      h.slows = h.slows.filter(s => s.until > this.t);
    }
  }

  /* ---------------- fixed step ---------------- */
  step(dt: number = STEP_DT) {
    if (this.winner) {
      this.winnerT += dt;
      if (this.winnerT > 6) this.reset();
      for (const h of this.heroes) this.updateYaw(h, dt);
      return;
    }
    this.t += dt;
    this.clock += dt;
    if (this.timers.length) {
      const due = this.timers.filter(x => x.at <= this.t);
      this.timers = this.timers.filter(x => x.at > this.t);
      due.forEach(x => x.fn());
    }
    // Every per-hero pass whose order can decide an outcome — own update, overlap push, bot thinking
    // (which casts on the spot) — follows the same order; the passes below it are order-free.
    const order = this.turnOrders[teamBFirst(Math.round(this.t / STEP_DT), this.seed) ? 1 : 0];
    for (const h of order) {
      const x = h.pos.x, z = h.pos.z;
      this.updateHero(h, dt);
      // Only the hero's own update counts as moving (walk, knockback, drag, hops) — being jostled by
      // separate() below is not.
      if (h.pos.x !== x || h.pos.z !== z) h.stillSince = this.t;
    }
    holdBonds(this);  // Связка: whatever moved them this tick, no further apart than allowed
    for (const m of this.monsters) updateMonster(this, m, dt);
    if (this.summons.length) { updateSummons(this, dt); countBirdSight(this, dt); }
    updateTeamBuffs(this);
    updateRelics(this, dt);
    this.separate(order);
    this.updateProjectiles(dt);
    this.updateZones(dt);
    this.updatePoints(dt);
    this.updateIncome(dt);
    for (const h of order) if (h.bot) this.think(h);
    // Runs last, for every hero including the dead, exactly like sim.js's drive(): it eases yaw toward
    // whatever wantYaw this same tick's movement/skills just set, instead of lagging a tick behind.
    for (const h of this.heroes) this.updateYaw(h, dt);
  }

  reset() {
    this.winner = null;
    this.score = { a: 0, b: 0 };
    this.ink = { a: 0, b: 0 };
    this.t = 0;  // every `until: t + dur` timestamp below is also being wiped, so nothing depends on t staying monotonic across a reset
    this.clock = 0; this.tickAcc = 0; this.overtime = false;
    this.points.forEach(p => { p.progress = 0; p.owner = null; p.contested = false; });
    this.pools = []; this.traps = []; this.projectiles = []; this.lobs = []; this.barriers = []; this.reveals = []; this.summons = [];
    this.totems = []; this.artifactLocks = []; this.timers = [];
    this.relics = { a: null, b: null }; this.eyeUntil = { a: 0, b: 0 };
    this.teamBuffs = noTeamBuffs();
    this.stats.reset();
    this.monsters.forEach(m => respawnMonster(this, m));
    this.heroes.forEach(h => {
      // Cleared BEFORE respawn(), not after: respawn() sets hp from maxHpOf(h), which reads h.artifacts and
      // h.boon — clearing them first means a hero who held Наплечник or a monster buff before this reset
      // doesn't respawn at their old boosted cap only to have it silently withdrawn a line later.
      h.artifacts = { E: null, R: null }; h.marks = []; h.boon = { ...NO_BOON };
      this.respawn(h);  // also clears pending/pendingTotem/invulnUntil/cloakUntil and any live skill state
      h.order = null; h.path = [];
      // Absolute timestamps from the previous match would otherwise stall until the restarted clock caught up.
      h.repathAt = 0; h.revealUntil = 0; h.revealedTo = { a: 0, b: 0 }; h.chainAt = undefined;
      if (h.bot) h.bot = { thinkAt: 0, retreat: false, lane: null, shopping: false };
    });
  }

  private updateHero(h: Hero, dt: number) {
    h.cd.attack = Math.max(0, h.cd.attack - dt);
    const cdt = dt * (cooldownRateOf(h) + eyeCdRateOf(this, h));  // Песочные часы, Око Бестиария
    h.cd.Q = Math.max(0, h.cd.Q - cdt);
    h.cd.W = Math.max(0, h.cd.W - cdt);
    if (h.artifacts.E) h.artifacts.E.cd = Math.max(0, h.artifacts.E.cd - cdt);
    if (h.artifacts.R) h.artifacts.R.cd = Math.max(0, h.artifacts.R.cd - cdt);
    if (!h.alive) { if (this.t >= h.respawnAt) this.respawn(h); return; }
    h.stateT += dt;
    if (h.stealthUntil > this.t && !this.inCover(h)) h.stealthUntil = 0;  // Слиться breaks out of cover
    if (h.kind === 'shadow' && this.reveals.some(r => r.team !== h.team && r.until > this.t && !this.revealShows(r, h)
      && Math.hypot(r.x - h.pos.x, r.z - h.pos.z) < r.r)) this.stats.passive(h.id, dt);  // Без отражения
    if (h.kb.lengthSq() > 0.01) {  // knockback slides the hero, stopped by walls
      const nx = h.pos.x + h.kb.x * dt, nz = h.pos.z + h.kb.z * dt;
      if (this.nav.isWalkable(nx, nz)) h.pos.set(nx, 0, nz); else h.kb.set(0, 0, 0);
      h.kb.multiplyScalar(Math.exp(-dt * KB_DECAY));
    }
    const [bx, bz] = LAYOUT.bases[h.team];
    if (Math.hypot(h.pos.x - bx, h.pos.z - bz) < RULES.fountainRadius) this.heal(h, RULES.fountainHeal * dt, undefined, true);
    // Base regen (always) plus Целебная мазь (out of combat): continuous like the fountain above, never
    // batched to a 0.5s pulse — outOfCombatRegenOf is 0 without the salve or before the quiet window.
    const regen = RULES.baseRegen + (h.kind === 'monk' ? PASSIVES.monk.regen : 0);  // Смирение
    const stealthRegen = this.stealthed(h) ? SKILLS.shadow.Q.regenPct : 0;  // Слиться
    this.heal(h, ((regen + stealthRegen) * maxHpOf(h) + outOfCombatRegenOf(h, this.t) + relicRegenOf(this, h)) * dt);  // + Сердце Гидры
    if (h.state === 'brace') this.heal(h, SKILLS.rhino.W.healPct * maxHpOf(h) * dt, h);  // Стойка
    if (h.pull) {  // Крюк: dragged to the ferryman
      const k = Math.min(1, (this.t - h.pull.t0) / h.pull.dur);
      h.pos.lerpVectors(h.pull.from, h.pull.to, k);
      if (k >= 1) h.pull = null;
      return;
    }
    if (this.t < h.stunUntil) return;
    if (updateSkill(this, h, dt)) return;
    if (h.forced && this.forcedWalk(h, dt)) return;
    if (this.taunted(h) && (!h.order || h.order.type !== 'attack' || h.order.target !== h.taunt!.by)) { h.order = { type: 'attack', target: h.taunt!.by }; h.repathAt = 0; }

    if (h.pending && this.t >= h.pending.at) this.resolveAttack(h);
    if (h.pendingTotem && this.t >= h.pendingTotem.at) this.resolveAttackTotem(h);
    const canAttack = h.state === 'normal' && this.t >= h.cloakUntil;  // Пергаментный плащ: can't attack while untargetable
    const o = h.order;
    if (o && (o.type === 'attack' || o.type === 'follow')) {
      const tg = o.target;
      // A silhouette (Без отражения) drops an enemy's attack order on her the moment she becomes one.
      const beyond = o.type === 'attack' && isHero(tg) && tg.team !== h.team && this.silhouette(tg);
      if (!tg.alive || beyond || (o.type === 'attack' && !this.targetVisibleTo(h.team, tg) && !this.taunted(h))) { h.order = null; h.path = []; }
      else {
        const d = flatDist(h.pos, tg.pos), reach = o.type === 'attack' ? this.attackRangeOf(h) + bulkOf(tg) : 2.2;
        if (d > reach) {
          if (this.t >= h.repathAt) { h.path = this.nav.findPath(h.pos.x, h.pos.z, tg.pos.x, tg.pos.z); h.repathAt = this.t + 0.3; }
        } else {
          h.path = [];
          if (o.type === 'attack' && canAttack) {
            h.wantYaw = yawTo(h.pos, tg.pos);
            if (h.cd.attack <= 0 && !h.pending) {
              if (d > h.def.range + bulkOf(tg)) this.stats.passive(h.id, 1);  // Сошка
              this.startAttack(h, tg);
            }
          }
        }
      }
    } else if (o && o.type === 'attackTotem') {
      const totem = this.totems.find(b => b.id === o.target);
      if (!totem || !this.totemVisibleTo(h.team, totem)) { h.order = null; h.path = []; }
      else {
        const d = flatDist(h.pos, totem.pos);
        if (d > this.attackRangeOf(h)) {
          if (this.t >= h.repathAt) { h.path = this.nav.findPath(h.pos.x, h.pos.z, totem.pos.x, totem.pos.z); h.repathAt = this.t + 0.3; }
        } else {
          h.path = [];
          if (canAttack) {
            h.wantYaw = yawTo(h.pos, totem.pos);
            if (h.cd.attack <= 0 && !h.pendingTotem) {
              if (d > h.def.range) this.stats.passive(h.id, 1);  // Сошка
              this.startAttackTotem(h, totem);
            }
          }
        }
      }
    }
    if (o && o.type === 'move' && !h.path.length) h.order = null;
    if (MOBILE_STATES.has(h.state)) this.walk(h, dt);
  }

  /* Charm walks the hero to whoever charmed it (stopping at arm's length), fear runs it home. Returns false
     once it has worn off, handing the hero back with no order. */
  private forcedWalk(h: Hero, dt: number): boolean {
    const f = h.forced as Forced;
    if (f.until <= this.t || (f.kind === 'charm' && !f.by.alive)) { h.forced = null; h.order = null; h.path = []; return false; }
    h.pending = null; h.pendingTotem = null;
    // Соблазн: Грех draws the charmed foe's life into himself — his own hit (credit, kill), healing what it took.
    if (f.drain && f.drainAt !== undefined && this.t >= f.drainAt) {
      f.drainAt += DRAIN_TICK;
      const before = Math.max(0, h.hp);
      this.damage(h, maxHpOf(h) * f.drain * DRAIN_TICK, f.by, { skill: true, skipSrcBuff: true });
      this.heal(f.by, before - Math.max(0, h.hp), f.by);
      if (!h.alive) return false;
    }
    const [bx, bz] = LAYOUT.bases[h.team];
    const goal = f.kind === 'charm' ? f.by.pos : { x: bx, z: bz };
    if (f.kind === 'charm' && flatDist(h.pos, goal) < 1.2) h.path = [];
    else if (this.t >= h.repathAt) { h.path = this.nav.findPath(h.pos.x, h.pos.z, goal.x, goal.z); h.repathAt = this.t + 0.3; }
    this.walk(h, dt);
    return true;
  }

  /* Facing: kept in core (it is simulation state, not a mesh) since dirTo()'s zero-length fallback and
     any server-authoritative "which way is this hero facing" reads depend on it. The rig/ring drawing
     that used to read it every frame (sim.js's drive()) is cut — that part really was rendering. */
  private updateYaw(h: Hero, dt: number) {
    const turn = angDiff(h.yaw, h.wantYaw);
    if (Math.abs(turn) < 0.02) h.snapTurn = false;
    const rate = h.def.turn * (h.snapTurn ? 2.5 : 1);
    h.yaw += Math.sign(turn) * Math.min(Math.abs(turn) * (1 - Math.exp(-dt * 16)), rate * dt);
  }

  private walk(h: Hero, dt: number) {
    if (!h.path.length || h.pending || h.pendingTotem) return;
    let step = this.speedOf(h) * dt;
    while (step > 0 && h.path.length) {
      const wp = h.path[0] as Point, dx = wp.x - h.pos.x, dz = wp.z - h.pos.z, d = Math.hypot(dx, dz);
      if (h.state === 'normal') h.wantYaw = Math.atan2(dx, dz);  // a braced or aiming hero keeps facing its target
      if (d <= step) { h.pos.set(wp.x, 0, wp.z); h.path.shift(); step -= d; }
      else { h.pos.x += dx / d * step; h.pos.z += dz / d * step; step = 0; }
    }
  }

  /* Pushes overlapping heroes apart once per tick, same as the prototype's once-per-frame version — but
     a tick is now always 1/60s, so this push is fixed-rate instead of frame-rate-dependent. Deliberate:
     that's what "fixed step" is for. */
  private separate(order: Hero[]) {
    const live = order.filter(h => h.alive);
    // Monsters never budge: an overlapping hero is pushed the whole way out of the monster's body.
    for (const m of this.monsters) {
      if (!m.alive) continue;
      const min = 0.45 + bulkOf(m);
      for (const h of live) {
        const d = flatDist(h.pos, m.pos);
        if (d > min || d < 1e-4) continue;
        const nx = h.pos.x + (h.pos.x - m.pos.x) / d * (min - d), nz = h.pos.z + (h.pos.z - m.pos.z) / d * (min - d);
        if (this.nav.isWalkable(nx, nz)) h.pos.set(nx, 0, nz);
      }
    }
    for (let i = 0; i < live.length; i++) for (let j = i + 1; j < live.length; j++) {
      const a = live[i] as Hero, b = live[j] as Hero, d = flatDist(a.pos, b.pos);
      if (d > 0.9 || d < 1e-4) continue;
      const push = (0.9 - d) / 2, nx = (b.pos.x - a.pos.x) / d, nz = (b.pos.z - a.pos.z) / d;
      if (this.nav.isWalkable(a.pos.x - nx * push, a.pos.z - nz * push)) { a.pos.x -= nx * push; a.pos.z -= nz * push; }
      if (this.nav.isWalkable(b.pos.x + nx * push, b.pos.z + nz * push)) { b.pos.x += nx * push; b.pos.z += nz * push; }
    }
  }

  private updatePoints(dt: number) {
    const R = LAYOUT.pointRadius;
    for (const p of this.points) {
      const inside = this.heroes.filter(h => h.alive && flatDist(h.pos, p.pos) < R);
      // A silhouette (Без отражения) holds a point but never takes it: she contests and stops the drift,
      // yet adds nothing to capture — neither progress alone nor the duo rate beside a teammate.
      const capturers = inside.filter(h => !this.silhouette(h));
      // Кукольник's golem holds a point like a silhouette: it contests and stops the drift, but never takes
      // it — no capture progress, alone or beside a teammate. No other summon counts.
      const golems = this.summons.filter(s => s.alive && s.kind === 'golem' && flatDist(s.pos, p.pos) < R);
      for (const h of inside) this.stats.onPoint(h.id, dt);
      const na = inside.filter(h => h.team === 'a').length + golems.filter(s => s.team === 'a').length;
      const nb = inside.length + golems.length - na;
      p.contested = na > 0 && nb > 0;
      if (!p.contested && capturers.length) {
        const n = capturers.length, rate = captureMultOf(capturers) / (n >= 2 ? RULES.captureDuo : RULES.captureSolo), dir = capturers[0]!.team === 'a' ? 1 : -1;
        p.progress = Math.max(-1, Math.min(1, p.progress + dir * rate * dt));
      } else if (!na && !nb) {
        const rest = p.owner === 'a' ? 1 : p.owner === 'b' ? -1 : 0;  // unattended progress drifts back
        p.progress += Math.sign(rest - p.progress) * Math.min(Math.abs(rest - p.progress), 0.1 * dt);
      }
      if (p.owner === 'a' && p.progress <= 0) p.owner = null;
      if (p.owner === 'b' && p.progress >= 0) p.owner = null;
      const was = p.owner;
      if (p.progress >= 1) p.owner = 'a';
      if (p.progress <= -1) p.owner = 'b';
      if (p.owner && p.owner !== was) {
        this.emit({ type: 'fx', name: 'capture', pos: this.pos2(p.pos) });
        this.stats.captured(capturers.filter(h => h.team === p.owner).map(h => h.id));
      }
    }
    this.tickAcc += dt;
    while (this.tickAcc >= RULES.pointTick) {
      this.tickAcc -= RULES.pointTick;
      for (const p of this.points) if (p.owner) this.score[p.owner] += 1;
    }
    const { a, b } = this.score;
    if (a >= RULES.win || b >= RULES.win) this.finish(a > b ? 'a' : 'b');
    else if (this.clock >= RULES.matchTime) {
      if (a !== b) this.finish(a > b ? 'a' : 'b');
      else this.overtime = true;
    }
  }

  finish(team: Team) { this.winner = team; this.winnerT = 0; }

  /* Ink: into each team's pool, flat + per point that team holds (docs/game-design.md, "Артефакты"). */
  private updateIncome(dt: number) {
    const held = { a: 0, b: 0 };
    for (const p of this.points) if (p.owner) held[p.owner]++;
    for (const team of ['a', 'b'] as const) this.ink[team] += dt * (INK.perSecond + INK.perPointPerSecond * held[team]);
  }

  /* ---------------- bots: decisions live in botBrain.ts / botGoals.ts ---------------- */
  private think(h: Hero) {
    if (!h.alive || this.t < (h.bot as BotState).thinkAt) return;
    if (h.state !== 'normal' && h.state !== 'charging' && h.state !== 'channel') return;
    (h.bot as BotState).thinkAt = this.t + 0.25;
    botThink(this, h, h.bot as BotState);
  }
  /* Тень: Слиться is useless in the open, so with a foe near she slips into close grass first, vanishes there
     and opens with the ambush swings; Тень на стене gets her home when hurt, or onto a wounded foe out of reach
     or one hitting her teammate. True = this think is spent; false = the ordinary bot logic runs. */
  shadowThink(h: Hero, bot: BotState): boolean {
    let foe: Hero | null = null, d = 12;
    for (const e of this.heroes) {
      const de = flatDist(e.pos, h.pos);
      if (e.team !== h.team && e.alive && de < d && this.visibleTo(h.team, e)) { foe = e; d = de; }
    }
    if (!foe) return false;
    if (h.hp < maxHpOf(h) * 0.35) {
      if (d < 7 && this.canCast(h, 'W')) {
        const [bx, bz] = LAYOUT.bases[h.team], k = Math.min(1, SKILLS.shadow.W.range / Math.max(1e-3, flatDist(h.pos, { x: bx, z: bz })));
        if (castSkill(this, h, 'W', { x: h.pos.x + (bx - h.pos.x) * k, z: h.pos.z + (bz - h.pos.z) * k })) bot.retreat = true;
      }
      return false;
    }
    if (bot.retreat) return false;
    // Stepping out of grass under enemy eyes ends Слиться: shoot from cover once in reach, else lie in wait.
    const reach = h.def.range + 1.5;
    if (this.stealthed(h)) {
      if (d <= reach || h.stealthUntil - this.t < 0.5) { if (h.order?.type !== 'attack' || h.order.target !== foe) this.orderAttack(h, foe); }
      else if (h.order) this.stop(h);
      return true;
    }
    // Back in 5 s: worth spending onto a wounded foe, or onto one hitting her teammate.
    const onMate = foe.order?.type === 'attack' && foe.order.target !== h && (foe.order.target as Hero).team === h.team;
    if ((foe.hp < maxHpOf(foe) * 0.5 || onMate) && d > h.def.range + 0.5 && d < SKILLS.shadow.W.range && this.canCast(h, 'W')) {
      if (castSkill(this, h, 'W', foe)) { this.orderAttack(h, foe); return true; }
    }
    if (h.cd.Q > 0) return false;
    if (this.canCast(h, 'Q')) {
      if (!castSkill(this, h, 'Q', null)) return false;
      if (d > reach) this.stop(h);
      return true;
    }
    if (inBrush(h.pos.x, h.pos.z)) return false;
    // Nearest grass within 6 m that isn't well away from the foe: a spot 0.6 of the patch's short radius in from
    // its centre, on her side, which is always inside the ellipse.
    let bx = 0, bz = 0, best = 6;
    for (const [cx, cz, rx, rz] of LAYOUT.brush) {
      const dc = Math.hypot(h.pos.x - cx, h.pos.z - cz), k = dc > 0 ? Math.min(dc, 0.6 * Math.min(rx, rz)) / dc : 0;
      const px = cx + (h.pos.x - cx) * k, pz = cz + (h.pos.z - cz) * k, dp = Math.hypot(h.pos.x - px, h.pos.z - pz);
      if (dp < best && Math.hypot(foe.pos.x - px, foe.pos.z - pz) <= d + 4 && this.nav.isWalkable(px, pz)) { bx = px; bz = pz; best = dp; }
    }
    if (best >= 6) return false;
    const end = h.path[h.path.length - 1];
    if (h.order?.type !== 'move' || !end || Math.hypot(end.x - bx, end.z - bz) > 1) this.orderMove(h, bx, bz);
    return true;
  }
}

